/**
 * Created at 2023/08/04 for v0.3 refactoring
 * from map base
 *
 * Removed majority of feature restyling function to `featureRender`,
 * leave style object functions and other simple function here!
 * @2023/09/12
 */
import Color from 'color';
import { Feature } from 'ol';
import { Coordinate } from 'ol/coordinate';
import { FeatureLike } from 'ol/Feature';
import {
  Point,
  Polygon,
  Geometry,
  MultiPoint,
  LineString,
  Circle,
  MultiLineString,
} from 'ol/geom';
import {
  Style,
  Fill,
  Stroke,
  Circle as CircleStyle,
  Text,
  RegularShape,
} from 'ol/style';
// import { StyleLike } from 'ol/style/Style';
import { TextPlacement } from 'ol/style/Text';

import { error, warn } from '@/utils/logger';

import {
  CATALOGUE_CATEGORY as CC,
  FEATURESTATE as FST,
  SymbolType,
  LENGTH_TYPE,
  SYMBOL_REVISION as REV,
  DETECTION_MODE as DTM,
} from '../types';
import { calculateCenter } from '../types/math';

import {
  FITTINGTYPE,
  FITTINGSIZE,
  FITTINGVER,
  FITTING_EC,
  DUCK_VERSION_NUMBER as DUCK,
  DEFAULT_LINE_COLOR,
  DEFAULT_DIAMOND_RADIUS,
  DEFAULT_EQUIPMENT_FONT_SIZE as DEF,
  DEFAULT_TRIANGLE_RADIUS,
  COMMON_STROKE_WIDTH as CSW,
  DEFAULT_NOTE_TXT,
  DEFAULT_EC_TXT as EC,
} from './config';

/** SYMBOL_PALETTE */
export enum PAL {
  /** user confirmed green */
  UCF = '#2ECC71',
  // ======================
  /** Missing any info Duct — Blue */
  MSD = '#4361EE',
  /** Missing any info Pipe — Blue */
  MSP = '#4361ee',
  // ======================
  /** Completed unconfirmed Duct — Pink */
  CUD = '#F18AB4',
  /** Completed unconfirmed Pipe — Pink */
  CUP = '#f18ab4',
  // ===========================
  /** Equipment — orange */
  EQM = '#FC913A',
  /** User added fittings — green */
  UAF = '#2eCC71',
  /** User added lines — green */
  UAL = '#2ecc71',
}

/**
 * // ! legacy font size algorithm:
 * FIXME: need a minimal font size for equipment
 * @date 2024/08/07
 * @log - make it smaller by multiply 0.6 in the hightlight showing text mode - @2025/08/22
 * @log - make it bigger by * 0.8 - @2025/09/19
 *
 * @param roundZL
 * @param ratio multiply ratio augument zoom level change - 2025/10/08
 * @returns
 */
const _zoomLevelToFontSize = (roundZL: number, ratio: number = 1) => {
  const endFontSize = Math.round(roundZL * roundZL * 0.8 * ratio);
  return Math.max(endFontSize, DEF);
};

/**
 * FIXME: need a minimal font size for equipment
 * @date 2024/08/07
 * @log - make it smaller by multiply 0.6 in the hightlight showing text mode - @2025/08/22
 * @log - make it bigger by * 0.8 - @2025/09/19
 *
 * @param roundZL
 * @param ratio multiply ratio augument zoom level change - 2025/10/08
 * @returns
 */
export const zoomLevelToFontSize = (roundZL: number, ratio: number = 1) => {
  // ! latest font size algorithm:
  // @2025/10/27
  const endFontSize = Math.pow(2, 1 + roundZL) * ratio;
  return Math.max(Math.round(endFontSize), DEF);
};

export type SymbolPalette = {
  fillColor: string;
  strokeColor: string;
  strokeWidth: number;
  /** for fill color */
  transparency: number;
};

type VersionPalette = { [key: string]: { strokeColor: string } };

export const lengthColors: { [key: string]: string } = {
  [LENGTH_TYPE.D]: PAL.UAL, // orange for duct length
  [LENGTH_TYPE.P]: PAL.UAL, // red for pipe length
  ['DEFAULT']: DEFAULT_LINE_COLOR, // gray
};

/**
 * v1 varaint palette, for missing info fittings(DUCT|PIPE)
 */
const variantPaletteV1: VersionPalette = {
  [CC.D]: {
    strokeColor: PAL.MSD,
  },
  [CC.P]: {
    strokeColor: PAL.MSP,
  },
  [LENGTH_TYPE.D]: {
    strokeColor: PAL.MSD,
  },
  [LENGTH_TYPE.P]: {
    strokeColor: PAL.MSP,
  },
};

/**
 * v2 varaint palette, for completed fittings(DUCT|PIPE)
 */
const variantPaletteV2: VersionPalette = {
  [CC.D]: {
    strokeColor: PAL.CUD, // pink: #F1948A
  },
  [CC.P]: {
    strokeColor: PAL.CUP,
  },
  [LENGTH_TYPE.D]: {
    strokeColor: PAL.CUD,
  },
  [LENGTH_TYPE.P]: {
    strokeColor: PAL.CUP,
  },
};

/**
 * User added dots and lines
 */
const variantPaletteM: VersionPalette = {
  [DTM.M]: {
    strokeColor: PAL.UAF,
  },
};

/**
 * DESIGN RULES:
 * a variant for `DUCT` and `PIPE` after version workflow, v1, v2 applied!
 *
 * @date 2024/03/19
 */
export const variantForState: { [version: string]: VersionPalette } = {
  [REV.B]: variantPaletteV1,
  [REV.C]: variantPaletteV2,
  [REV.M]: variantPaletteM,
};

const strokeParency = {
  strokeWidth: 1,
  transparency: 0.002,
};

/**
 * Version could be color name to act as a feature theme
 *
 * @date 2023/11/16
 */
export const versionPalette: { [key: string]: SymbolPalette } = {
  // duck feature version, could be brush color?
  [DUCK]: {
    fillColor: '#000000',
    strokeColor: DEFAULT_LINE_COLOR,
    strokeWidth: 2,
    transparency: 0.002,
  },
  [REV.A]: {
    fillColor: '#ed1941',
    strokeColor: '#aa2116',
    strokeWidth: 2,
    transparency: 1,
  },
  [REV.B]: {
    fillColor: '#000000', // black
    strokeColor: '#0000FF', //  blue
    strokeWidth: CSW,
    transparency: 0.001, // FIXME: remove box shading - 2024/03/04
  },
  [REV.C]: {
    fillColor: '#FCAF17', // orange
    strokeColor: PAL.CUD, // pink for completed symbol
    strokeWidth: CSW,
    transparency: 0.002,
  },
  [REV.D]: {
    fillColor: '#03C04A', // light green
    strokeColor: PAL.UCF, // grey green from simon feedback - @2024/03/06
    strokeWidth: CSW,
    transparency: 0.002,
  },
  [REV.M]: {
    fillColor: '#000000', // black
    strokeColor: '#0000FF', //  blue, will be overrided by variant palette
    strokeWidth: CSW,
    transparency: 0.002,
  },
  [REV.BL]: {
    fillColor: '#041BC5', // blue
    strokeColor: '#041BC5', // blue
    ...strokeParency,
  },
  [REV.G]: {
    fillColor: '#479C4C', // green
    strokeColor: '#479C4C', // green
    ...strokeParency,
  },
  [REV.O]: {
    fillColor: '#ED662C', // orange
    strokeColor: '#ED662C', // orange
    ...strokeParency,
  },
  // === for newly added symbols ===
  [REV.P]: {
    fillColor: '#B241BC', // purple
    strokeColor: '#B241BC', // purple
    ...strokeParency,
  },
  [REV.R]: {
    fillColor: '#D32E39', // red
    strokeColor: '#D32E39', // red
    ...strokeParency,
  },
  [REV.Y]: {
    fillColor: '#F9E54D', // yellow
    strokeColor: '#F9E54D', // yellow
    ...strokeParency,
  },
};

export const getDucPalette = () => versionPalette[DUCK];

// set different border color for fittings ...
const strokeColors: { [key: string]: string } = {
  Duct: 'blue',
  Pipe: 'red',
};

export const isPolygon = (geometry: Geometry | undefined) => {
  return geometry instanceof Polygon;
};

export const isLineString = (geometry: Geometry | undefined) => {
  return geometry instanceof LineString;
};

export const isMultiLineString = (geometry: Geometry | undefined) => {
  return geometry instanceof MultiLineString;
};

export const isCircle = (geometry: Geometry | undefined) => {
  return geometry instanceof Circle;
};

export const isPoint = (geometry: Geometry | undefined) => {
  return geometry instanceof Point;
};

export const isTriangleFeature = (feature: Feature) => {
  const symbolType = feature.get(FITTINGTYPE) as SymbolType;
  return symbolType === SymbolType.TRIANGLE;
};

export const isDiamondFeature = (feature: Feature) => {
  const symbolType = feature.get(FITTINGTYPE) as SymbolType;
  return symbolType === SymbolType.DIAMOND;
};

/**
 * Check current feature if active or not
 * @param feature
 * @returns true, false
 */
export const isActive = (feature: Feature | FeatureLike) => {
  return !!feature.get(FST.A);
};

export const isDisabled = (feature: Feature | FeatureLike) => {
  const disabled = feature.get(FST.D);
  if (disabled) return true;
  return false;
};

export const isDuckFeature = (feature: Feature | FeatureLike) => {
  const version = feature.get(FITTINGVER);
  return version === DUCK;
};

export const isPointFeature = (feature: Feature | FeatureLike) => {
  const geometry = feature.getGeometry();
  return isPoint(geometry as Geometry);
};

export const isEquipmentFeature = (feature: Feature) => {
  const geometry = feature.getGeometry();
  const symbolType = feature.get(FITTINGTYPE) as SymbolType;
  const isEquipment = symbolType === SymbolType.EQUIPMENT;
  return isPoint(geometry as Geometry) && isEquipment;
};

export const isNoteFeature = (feature: Feature) => {
  const geometry = feature.getGeometry();
  const symbolType = feature.get(FITTINGTYPE) as SymbolType;
  const isNote = symbolType === SymbolType.NOTE;
  return isPolygon(geometry) && isNote;
};

/**
 * @deprecated 2024/03/19
 * @param symbol
 * @returns
 */
export const getStrokeColor4Feature = (symbol: Feature) => {
  const detail = symbol.get('detail') || {};
  const category = detail['category']; // symbol category
  return strokeColors[category] || 'blue';
};

/**
 * For `activeFeature` in featureRender use
 * @param feature
 * @returns
 */
export const getActivePointStyle = (feature: Feature): Style | undefined => {
  // FIXME: this could be null, no idea in which case?
  // @2024/07/23
  const origStyle = feature.getStyle() as Style;
  if (!origStyle) {
    error(`## got null orig style!`);
    return undefined;
  }
  const origText = (origStyle as Style).getText();

  // == triangle or diamond symbol ==
  if (!origText) {
    // console.log(`>> to recreate red image style !`);
    // FIXME: use original image and modify its color!
    // to fixe issue https://taksoai.atlassian.net/browse/TAK-541
    // @2024/04/02
    // FIXME: Weird ! GOT ERROR in this issue:
    // https://taksoai.atlassian.net/browse/TAK-558
    // @2024/04/04
    const image = origStyle.getImage() as RegularShape;
    if (!image) {
      warn(`## No image shape return!`);
      return origStyle;
    }
    const fill = new Fill({ color: 'rgba(255, 0, 0, 0.6)' });
    const stroke = new Stroke({ color: 'red', width: 2 });
    image.setFill(fill);
    image.setStroke(stroke);
    return new Style({ image });
  }
  // == for equipment setting ==
  const redCenter = new CircleStyle({
    radius: 6, // smaller for dot circle feature @2022/11/30
    fill: new Fill({
      color: '#ff3333', // red center
    }),
  });
  return new Style({ text: origText, image: redCenter });
};

const composeFont = (fontSize: number, italic?: boolean) => {
  return `bold ${italic ? 'italic' : ''} ${fontSize}px "Open Sans", "Arial Unicode MS", "sans-serif"`;
};

/**
 * build a text object for feature style
 * @param label text
 * @param size font size
 * @param italic
 * @param placement
 * @param isRichFormat
 * @param isActive for highlight style
 * @param color for text color, green by defaut
 *
 * @returns
 */
export const basicTextStyle = (
  label: string,
  size?: number,
  italic?: boolean,
  placement: TextPlacement = 'point',
  isRichFormat = false,
  isActive = false,
  color = 'green',
  offsetY = 0,
): Text => {
  const fontSize = size || DEF;
  const options = {
    font: composeFont(fontSize, italic),
    placement,
    fill: new Fill({
      color,
    }),
    offsetX: 0,
    offsetY,
    overflow: true, // allowing text overlap, and display more
    stroke: new Stroke({
      color: 'white',
      width: 1,
    }),
    text: label || 'MOCK',
    textAlign: 'left',
  } as any;
  // more format to create a box,
  const richOpts = {
    // ** final text color, will override the `fill` attribute in `options`!
    fill: new Fill({
      color: 'coral',
    }),
    // override the default options
    placement: 'point' as TextPlacement,
    // == box border color: ==
    // ** available while placement is point only
    backgroundStroke: new Stroke({
      color: isActive ? color : 'orange',
      width: 1,
    }),
    // available while placement is point only
    backgroundFill: new Fill({
      color: isActive ? [255, 0, 0, 0.1] : [255, 255, 255, 0.1],
    }),
    padding: [1, 1, 1, 1],
    // NOTE: keep text algin left to make text looks stable
    // @2024/04/29, @2025/10/17
    textAlign: 'left',
    textBaseline: 'top',
  };
  const endOptions = isRichFormat ? { ...options, ...richOpts } : options;
  return new Text(endOptions);
};

/**
 * Make a complext text renderer function for equipment `multiplier` display.
 *
 * @param label equipment text
 * @param multiplier right-top corner text: x?
 * @param fontSize font size
 * @param fontColor font color
 * @param isActive if highligt
 */
export const complexTextStyle = (
  label: string,
  multiplier: string = '',
  fontSize: number,
  fontColor: string,
  isActive: boolean | undefined,
) => {
  // ! This radio(0.5) can make selected equipment text size looks normal!
  const seletedSizeChangeRatio = 0.5;
  return new Style({
    // ! Need to keep simple text style to make point feature have selected style
    text: basicTextStyle(
      label,
      fontSize * seletedSizeChangeRatio,
      false,
      'point',
      true,
      true,
      'green',
      0, // must be `0`, otherwise the text box have wierd offset!
    ),
    renderer: function (coordinates, state) {
      const [x0, y0] = coordinates as Coordinate;
      const { context: ctx } = state;
      ctx.save();
      // setup font size then measure text!
      ctx.font = composeFont(fontSize);
      const spaceTotal = 12;
      // get real width after context get the `font`
      const metrics = ctx.measureText(label);
      const boxWidth = metrics.width + spaceTotal;
      const boxHeight = fontSize + spaceTotal;
      // ! fix the selected equipment offset!
      // @2025/11/10
      const startX = x0 - spaceTotal / 2;
      const startY = y0 - spaceTotal / 2;
      // Draw a filled rectangle
      ctx.fillStyle = `rgba(255, 255, 255, 0.1)`;
      if (isActive) {
        ctx.fillStyle = `rgba(255, 0, 0, 0.1)`;
      }
      // Draw a blue filled square (x, y, width, height)
      ctx.fillRect(startX, startY, boxWidth, boxHeight);

      // Draw an outlined rectangle
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'orange'; // Set the stroke color
      if (isActive) {
        ctx.strokeStyle = 'green';
      }
      // Draw border
      ctx.strokeRect(startX, startY, boxWidth, boxHeight);

      // draw text with `black` color
      ctx.fillStyle = fontColor;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText(label, x0, y0);

      // draw top-right corner `multiplier` superscript if exists
      if (multiplier) {
        const thrink = 0.8;
        ctx.fillStyle = 'red';
        ctx.font = composeFont(fontSize * thrink);
        ctx.fillText(
          `x${multiplier}`,
          x0 + metrics.width,
          y0 - fontSize * thrink,
        );
      }

      ctx.restore();
    },
  });
};

const wrapText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  /** fontSize*1.5 */
  lineHeight: number,
) => {
  const words = text.split(' ');
  let line = '';

  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + ' ';
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;

    if (testWidth > maxWidth && n > 0) {
      ctx.fillText(line, x, y);
      line = words[n] + ' ';
      y += lineHeight;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line, x, y);
};

/**
 * Custom note style builder based on renderer function
 * @param bgColor
 * @returns
 */
export const baseNoteRenderStyle = (
  bgColor: string = 'orange',
  text: string = DEFAULT_NOTE_TXT,
  fontSize: number = 14,
) => {
  const color = Color(bgColor);
  const red = color.red();
  const green = color.green();
  const blue = color.blue();
  const opacity = 0.5;
  return new Style({
    renderer: function (coordinates, state) {
      const [[tl, tr, br, bl]] = coordinates as Coordinate[][];
      const { context: ctx } = state;
      const [x0, y0] = tl;
      const width = tr[0] - tl[0];
      const height = bl[1] - tr[1];
      ctx.save();

      // Draw a filled rectangle
      ctx.fillStyle = `rgba(${red}, ${green}, ${blue}, ${opacity})`;
      // Draw a blue filled square (x, y, width, height)
      ctx.fillRect(x0, y0, width, height);

      // Draw an outlined rectangle
      ctx.strokeStyle = 'green'; // Set the stroke color
      // default boder size 1px
      ctx.lineWidth = 2;
      ctx.strokeRect(x0, y0, width, height);

      // draw text with `black` color
      ctx.font = composeFont(fontSize);
      ctx.fillStyle = 'black';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      // ctx.fillText(text, x0 + 8, y0 + 8);
      wrapText(ctx, text, x0 + 4, y0 + 4, width - 4, fontSize * 1.4);

      ctx.restore();
    },
  });
};

export const basicPolygonStyle = (
  active = false,
  strokeColor = 'blue',
  strokeWidth = 1,
): Style => {
  return new Style({
    /**
     * NOTE: Remember to keep this `geometry` property!
     * It will allow the rectangle symbol to resize in a rectanglar shape!
     * Once removed this part then it caused a bug:
     * https://taksoai.atlassian.net/browse/TAK-501
     *
     * @param feature
     * @returns
     */
    geometry: function (feature) {
      const modifyGeometry = feature.get('modifyGeometry');
      return modifyGeometry ? modifyGeometry.geometry : feature.getGeometry();
    },
    fill: new Fill({
      color: 'rgba(0, 0, 0, 0.01)', // transparent black
    }),
    stroke: new Stroke({
      color: active ? '#3F99F7' : strokeColor, // sky blue line
      width: strokeWidth,
    }),
  });
};

/**
 * NOT IN USE
 * @deprecated
 * @param center
 * @returns
 */
export const basicCircleImage = (center: Coordinate): Style => {
  return new Style({
    image: new CircleStyle({
      radius: 4,
      fill: new Fill({
        color: '#FFFFFF', // white
      }),
    }),
    geometry: new Point(center),
  });
};

export const basicCircleStyle = (
  strokeColor = 'green',
  strokeWidth = 2,
): Style => {
  return new Style({
    stroke: new Stroke({
      color: strokeColor,
      width: strokeWidth,
    }),
    fill: new Fill({
      color: 'green',
    }),
  });
};

export const basicECStyle = (ecTxt: string) => {
  return new Style({
    text: basicTextStyle(ecTxt, 10, false, 'point', false, false, 'red'),
  });
};

export const basicTriangleStyle = (
  strokeColor = 'blue',
  strokeWidth = 2,
  radius = DEFAULT_TRIANGLE_RADIUS,
): Style => {
  const stroke = new Stroke({ color: strokeColor, width: strokeWidth });
  const fill = new Fill({ color: 'rgba(255, 0, 0, 0.01)' });
  return new Style({
    image: new RegularShape({
      fill: fill,
      stroke: stroke,
      points: 3,
      radius,
      rotation: Math.PI / 4,
      angle: 0,
    }),
  });
};

export const basicDiamonStyle = (
  strokeColor = 'blue',
  strokeWidth = 2,
  radius = DEFAULT_DIAMOND_RADIUS,
): Style => {
  const stroke = new Stroke({ color: strokeColor, width: strokeWidth });
  const fill = new Fill({ color: 'rgba(255, 0, 0, 0.01)' });
  return new Style({
    image: new RegularShape({
      fill: fill,
      stroke: stroke,
      points: 4,
      radius,
      angle: 0,
    }),
  });
};

/**
 * Reset linestring or polygon style after selected
 * @param feature linestring or polygon
 * @returns
 */
export const modifyStateFunction = (feature: FeatureLike): Style[] => {
  const basicStyle = basicPolygonStyle(false, 'green', 3);
  basicStyle.setFill(
    new Fill({
      color: 'rgba(0, 255, 0, 0.5)', // translucent green
    }),
  );
  const isCircle = feature.getGeometry() instanceof Circle;
  const circleStyle = basicCircleStyle('orange', 2);
  // === basic style ===
  const styles = [isCircle ? circleStyle : basicStyle];
  const modifyGeometry = feature.get('modifyGeometry');
  const geometry = modifyGeometry
    ? modifyGeometry.geometry
    : feature.getGeometry();
  const result = calculateCenter(geometry);
  const center = result.center;
  if (center.length > 0) {
    // == add middle dot ==
    styles.push(
      new Style({
        geometry: new Point(center),
        image: new CircleStyle({
          radius: 4, // smaller for dot circle feature @2022/11/30
          fill: new Fill({
            color: '#ff3333', // red center
          }),
        }),
      }),
    );
    const coordinates = result.coordinates;
    // == add end dots ==
    if (coordinates) {
      const { minRadius, sqDistances } = result;
      const rsq = minRadius * minRadius;
      const points = coordinates.filter(function (coordinate, index) {
        return sqDistances[index] > rsq;
      });
      styles.push(
        new Style({
          geometry: new MultiPoint(points),
          image: new CircleStyle({
            radius: 6, // bigger for convenience of handling
            fill: new Fill({
              color: '#ffcc33', // yellow handler
            }),
          }),
        }),
      );
    }
  }
  // == add E.C. rendering - @2025/11/11 ==
  const hasEC = feature.get(FITTING_EC);
  if (hasEC) {
    const ec = basicECStyle(EC);
    styles.push(ec);
  }
  return styles;
};

/**
 * Create line style with palette color `PAL.UAL`
 * @date 2024/03/21
 *
 * @param color color string
 * @param stroke stroke size
 * @returns
 */
export const basiceLengthStyle = (
  color: string = PAL.UAL,
  stroke?: number,
): Style => {
  return new Style({
    geometry: function (feature) {
      const modifyGeometry = feature.get('modifyGeometry');
      return modifyGeometry ? modifyGeometry.geometry : feature.getGeometry();
    },
    stroke: new Stroke({
      color,
      width: stroke || 3,
    }),
  });
};

/**
 * Update text style for feature from `resetFeatureStyle/featureUpdater`
 *
 * @param style
 * @param label
 * @param fontSize
 * @param placement
 * @param isRichFormat
 */
export const updateStyleText = (
  style: Style,
  label: string,
  fontSize?: number,
  // ! MUST be `point` for polygon feature !!!
  // ! OTHERWISE the text style does not show in smaller rectangular - @2025/11/04
  placement: TextPlacement = 'point',
  isRichFormat = false,
) => {
  // ! need an offset to move size text above the symbol!
  // @2025/11/06
  const yOffset = -8;
  const text = basicTextStyle(
    label || '(:',
    fontSize,
    false,
    placement,
    isRichFormat,
    false,
    'green',
    yOffset,
  );
  style.setText(text);
  return text;
};

/**
 * Set eraser style by stroke width
 *
 * @date 2024/04/03
 * @param width dynamic stroke width decided by zoom level
 * @returns
 */
export const eraseStyle = (width: number) => {
  return new Style({
    fill: new Fill({
      color: '#FFFFFF', // white
    }),
    stroke: new Stroke({
      color: '#FFFFFF', // white
      width,
    }),
  });
};

/**
 * NOT IN USE
 *
 * @deprecated
 * @returns
 */
export const basiceLineStyle = (): Style => {
  return new Style({
    geometry: function (feature) {
      const modifyGeometry = feature.get('modifyGeometry');
      return modifyGeometry ? modifyGeometry.geometry : feature.getGeometry();
    },
    stroke: new Stroke({
      color: 'blue', // sky blue line
      width: 2,
    }),
  });
};

/**
 * Gray blue sketch line style
 *
 * @date 2024/05/14
 * @returns
 */
export const minimalStyle = (): Style => {
  const stroke = new Stroke({ color: '#444C87', width: 1 });
  return new Style({ stroke });
};
